import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { CircularLoader } from '../components/loader/CircularLoader';
import { useAuthContext, useAuthInitializedContext } from '../hooks/use-user-auth-context.hooks';
import { CustomUser } from '../redux/slice/auth/CustomUser';
import {
  dispatchCareerRoadMapAssessment,
  dispatchGetCareerRoadMapAssessment,
} from '../redux/slice/career-roadmap/career-roadmap.operations';
import { dispatchGetLatestRoadmapAI } from '../redux/slice/roadmap-ai/roadmap-ai.operations';
import { fetchLatestRoadmapFromAPI } from '../redux/slice/roadmap-ai/roadmap-ai.thunk';
import { dispatchGetUserProfileValues } from '../redux/slice/user-profile';
import { RootState, useAppDispatch } from '../redux/store';

export const GenerateRoadmapPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user: CustomUser | null = useAuthContext();
  const { isRoadmapAiGenerated } = useSelector((state: RootState) => state.careerRoadmap);
  const isAuthenticated = user ? true : false;
  const isAuthInitialized: boolean = useAuthInitializedContext();

  const [showConfetti, setShowConfetti] = useState(false);
  const [isGenerated, setIsGenerated] = useState(isRoadmapAiGenerated || false);
  const [isLoading, setIsLoading] = useState(false);

  // Navigate if roadmap already generated

  useEffect(() => {
    if (!isAuthInitialized) {
      navigate('/login');
    }
  }, [isAuthInitialized]);
  useEffect(() => {
    if (isRoadmapAiGenerated) {
      setIsGenerated(true);
      navigate('/roadmap-view');
    }
  }, [isRoadmapAiGenerated, navigate]);

  const handleGenerateRoadmap = async () => {
    try {
      setIsGenerated(false);
      setIsLoading(true);
      if (user?.user.uid) {
        const data = await dispatch(fetchLatestRoadmapFromAPI(user?.user.uid)).unwrap();
        if (data && data.roadMap.length) {
          await dispatch(dispatchCareerRoadMapAssessment(true, 'isRoadmapAiGenerated'));
          setIsGenerated(true);
          navigate('/roadmap-view');
        }
      } else {
        toast.error('Something went wrong');
      }
    } catch (error) {
      setIsLoading(false);
      toast.error('Something went wrong');
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    if (isAuthenticated) {
      void dispatch(dispatchGetUserProfileValues());
      void dispatch(dispatchGetCareerRoadMapAssessment());
      void dispatch(dispatchGetLatestRoadmapAI());
    }
  }, [isAuthenticated, dispatch]);

  // Confetti + micro-line timing
  useEffect(() => {
    const confettiTimer = setTimeout(() => {
      setShowConfetti(true);
      const microLineTimer = setTimeout(() => {
        setShowConfetti(false);
      }, 25000); // 1-second confetti burst

      return () => clearTimeout(microLineTimer);
    }, 1000); // start 1s after header fade-in

    return () => clearTimeout(confettiTimer);
  }, []);

  return (
    <div className="bg-gradient-to-bl from-purple-400 to-purple-100 relative py-10 flex flex-col items-center justify-center  px-6 text-center  overflow-hidden">
      {isLoading && <CircularLoader />}
      <div>
        {/* Confetti Burst */}
        {showConfetti && (
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {[...Array(40)].map((_, i) => (
              <span
                key={i}
                className={`absolute block w-5 h-5 rounded-full opacity-10 animate-fall`}
                style={{
                  left: `${Math.random() * 100}%`,
                  backgroundColor: ['#C5B4E3', '#B6E3C5', '#EAEAEA'][Math.floor(Math.random() * 8)],
                  animationDelay: `${Math.random()}s`,
                  animationDuration: `${5 + Math.random() * 0.5}s`,
                }}
              ></span>
            ))}
          </div>
        )}

        {/* Header */}
        <motion.h1
          className="text-2xl md:text-5xl font-bold text-center text-gray-800 mb-6"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 2 }}
        >
          🎉 Roadmap Unlocked!
        </motion.h1>

        {/* Body */}
        <motion.div
          className="max-w-md md:max-w-2xl text-gray-700 text-base md:text-xl leading-relaxed"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 1 }}
        >
          <p className="mb-4 font-medium">You did it — and this is just the beginning.</p>
          <p className="mb-4 font-medium">
            Your personalized career roadmap is live, built from your goals, skills, and strengths.
          </p>
          <p className="mb-4 font-medium">
            Every choice you make helps your roadmap learn what drives you most.
          </p>
          <p className="mb-4 font-medium">
            It grows as you do — unlocking new insights whenever you check in or explore something
            new.
          </p>
          <p className="mb-6 font-semibold text-gray-900">Keep exploring. Keep evolving.</p>
          <p className="italic font-semibold text-gray-500">
            The next version of your future is already waiting.
          </p>
        </motion.div>

        {/* Button */}
        <motion.button
          className="btn-internal mt-10 px-8 py-3 rounded-2xl button text-gray-800 font-semibold shadow-md hover:shadow-lg btn-internal:hover transition-all"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onClick={handleGenerateRoadmap}
          disabled={isGenerated}
        >
          See My Roadmap
        </motion.button>

        {/* Sub-line */}
        <motion.p
          className="mt-6 text-lg font-semibold text-gray-500"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
        >
          → Come back anytime — your roadmap updates as your journey unfolds.
        </motion.p>

        {/* Tailwind CSS keyframes */}
        <style>
          {`
          @keyframes fall {
            0% { transform: translateY(-10px) rotate(90deg); opacity: 1; }
            100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
          }
          .animate-fall { animation: fall linear forwards; }
        `}
        </style>
      </div>
    </div>
  );
};
